import React, { useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './button.tsx';
import { cn } from '../../lib/utils.ts';
import { YearSelect } from './year-select.tsx';

// interface Event {
//   description: 'stuff';
//   endTime: '2025-04-11T11:08:57.524Z';
//   eventDate: '2025-04-11T10:08:57.524Z';
//   eventId: '6838a41774afa15466023c1f';
//   eventTitle: 'stuff';
//   eventType: 'Event';
//   location: 'hall';
//   organizationId: '68063516a993fd54bd5274f8';
//   recurrenceRule: 'FREQ=WEEKLY;BYDAY=MO,WE,FR';
//   recurring: true;
//   startTime: '2025-04-11T10:08:57.524Z';
// }

interface Event {
  description: string;
  endTime: string;
  eventDate: string;
  eventId: string;
  eventTitle: string;
  eventType: string;
  location: string;
  organizationId: string;
  recurrenceRule: string;
  recurring: boolean;
  startTime: string;
}
interface CalendarProps {
  mode?: 'single';
  selected?: Date | undefined;
  onSelect?: (date: Date | undefined) => void;
  disabled?: (date: Date) => boolean;
  className?: string;
  events: Event[];
}

const DAYS = [
  { key: 'sun', label: 'S' },
  { key: 'mon', label: 'M' },
  { key: 'tue', label: 'T' },
  { key: 'wed', label: 'W' },
  { key: 'thu', label: 'T' },
  { key: 'fri', label: 'F' },
  { key: 'sat', label: 'S' },
];

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function Calendar({
  mode = 'single',
  selected,
  onSelect,
  disabled,
  className,
  events,
}: CalendarProps) {
  const [currentDate, setCurrentDate] = React.useState(new Date());
  const [eventDates, setEventDates] = React.useState<Set<string>>(
    new Set<string>()
  );

  useEffect(() => {
    events?.forEach((event) => {
      setEventDates((eventDates) =>
        new Set(eventDates).add(event.eventDate.split('T')[0])
      );
    });
  }, [events]);

  // Calendar calculations
  const daysInMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  ).getDate();
  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  ).getDay();

  // Navigation handlers
  const prevMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const nextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const handleYearChange = (year: string) => {
    const newDate = new Date(currentDate);
    newDate.setFullYear(parseInt(year));
    setCurrentDate(newDate);
  };

  // Date state handlers
  const isSelected = (day: number) => {
    if (!selected) return false;
    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    return date.toDateString() === selected.toDateString();
  };

  const isToday = (day: number) => {
    const today = new Date();
    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    return date.toDateString() === today.toDateString();
  };

  const isEventToday = (day: number) => {
    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    return eventDates.has(date.toISOString().split('T')[0]);
  };

  const isDisabled = (day: number) => {
    if (!disabled) return false;
    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    date.setHours(12, 0, 0, 0);
    return disabled(date);
  };

  const handleDateClick = (day: number) => {
    if (!onSelect || isDisabled(day)) return;
    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    date.setHours(12, 0, 0, 0);
    onSelect(date);
  };

  // Years for dropdown
  const years = Array.from({ length: 100 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return { value: year.toString(), label: year.toString() };
  });

  return (
    <div
      className={cn(
        'w-full bg-white rounded-lg shadow-sm border border-gray-100',
        'p-3 select-none',
        className
      )}
    >
      {/* Header */}
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            size="icon"
            onClick={prevMonth}
            className="h-7 w-7 hover:bg-gray-100 hover:text-purple-600 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold text-gray-700 min-w-[36px]">
              {MONTHS[currentDate.getMonth()].substring(0, 3)}
            </span>
            <YearSelect
              value={currentDate.getFullYear().toString()}
              onValueChange={handleYearChange}
              options={years}
              className="h-7 w-[4.5rem] text-gray-700 hover:border-purple-300 transition-colors"
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={nextMonth}
            className="h-7 w-7 hover:bg-gray-100 hover:text-purple-600 transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Calendar */}
      <div>
        {/* Days header */}
        <div className="grid grid-cols-7 gap-1 mb-1.5">
          {DAYS.map((day) => (
            <div
              key={day.key}
              className="text-center text-xs font-medium text-gray-500 mb-1"
            >
              {day.label}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells for first week */}
          {Array.from({ length: firstDayOfMonth }).map((_, index) => (
            <div key={`empty-${index}`} className="h-7" />
          ))}

          {/* Date cells */}
          {Array.from({ length: daysInMonth }).map((_, index) => {
            isEventToday(index + 1);
            const day = index + 1;
            return (
              <Button
                key={day}
                variant="ghost"
                onClick={() => handleDateClick(day)}
                disabled={isDisabled(day)}
                className={cn(
                  'h-7 w-7 p-0 font-normal text-sm rounded-md transition-all duration-200',
                  'hover:bg-purple-50 hover:text-purple-600 focus:ring-2 focus:ring-purple-200',
                  isEventToday(index + 2) &&
                    'border-2 border-red-200 text-red-600 font-medium',
                  isSelected(day) &&
                    'bg-purple-600 text-white hover:bg-purple-700 hover:text-white',
                  isToday(day) &&
                    !isSelected(day) &&
                    'border-2 border-purple-200 text-purple-600 font-medium',
                  isDisabled(day) &&
                    'opacity-50 cursor-not-allowed hover:bg-transparent hover:text-gray-400',
                  !isSelected(day) && !isDisabled(day) && 'text-gray-700'
                )}
              >
                {day}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
